import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { API, api, fmtDate, money } from "@/lib/api";
import { PageHead } from "./Layout";

export const STATUS_STYLE: Record<string, string> = {
  new: "bg-sky-100 text-sky-900", contacted: "bg-amber-100 text-amber-900", won: "bg-emerald-100 text-emerald-900", lost: "bg-zinc-200 text-zinc-700",
};
const FILTERS = ["all", "new", "contacted", "won", "lost"];
const STAGES = [["", "All stages"], ["started", "Started"], ["measuring", "Measuring"], ["completed", "Completed"], ["requested", "Service requested"], ["manual", "Manual review"], ["draft", "Field draft"]];
const SOURCES = [["", "All sources"], ["website_widget", "Website Widget"], ["field_quote", "Field Quote"]];
export const sourceLabel = (l: any) => (l.lead_source === "field_quote" ? "Field Quote" : "Website Widget");

export function stageOf(l: any): { label: string; cls: string } {
  if (l.kind === "manual_review") return { label: "Manual Review", cls: "bg-violet-100 text-violet-900" };
  if (l.service_requested_at) return { label: "Service Requested", cls: "bg-emerald-600 text-white" };
  if (l.estimate_stage === "draft") return { label: "Field Quote · Draft", cls: "bg-slate-200 text-slate-800" };
  if (l.estimate_stage === "started") return { label: "Started Estimate", cls: "bg-orange-100 text-orange-900" };
  if (l.estimate_stage === "measuring") return { label: "Started Estimate · Measuring", cls: "bg-orange-100 text-orange-900" };
  return { label: "Completed Estimate", cls: "bg-teal-100 text-teal-900" };
}

export const qtyText = (l: any) => (l.measured_quantity || l.net_area_sq_ft) ? `${Math.round(Number(l.measured_quantity || l.net_area_sq_ft)).toLocaleString("en-US")} ${l.measured_unit === "linear_ft" ? "linear ft" : "sq ft"}` : "—";

export default function Leads() {
  const nav = useNavigate();
  const [status, setStatus] = useState("all");
  const [stage, setStage] = useState("");
  const [source, setSource] = useState("");
  const [items, setItems] = useState<any[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async (c: string | null, append: boolean) => {
    setLoading(true);
    const { data } = await api.get("/orgs/me/leads", { params: { status: status === "all" ? undefined : status, stage: stage || undefined, source: source || undefined, cursor: c || undefined, page_size: 25 } });
    setItems((prev) => (append ? [...prev, ...data.items] : data.items));
    setCursor(data.next_cursor);
    setLoading(false);
  }, [status, stage, source]);
  useEffect(() => { load(null, false); }, [load]);
  const price = (l: any) => (l.final_price_cents ? <span>{money(l.final_price_cents)} <span className="text-[10px] text-amber-700">adjusted</span></span> : l.estimate_cents == null ? <span className="text-xs text-muted-foreground">—</span> : l.low_cents === l.high_cents ? money(l.estimate_cents) : `${money(l.low_cents)}–${money(l.high_cents)}`);
  return (
    <div className="fade-up">
      <PageHead title="Leads" sub="Every homeowner request from your widget and hosted page, plus quotes you create in the field."
        actions={<Button variant="outline" asChild data-testid="export-csv-button"><a href={`${API}/orgs/me/leads/export.csv`}><Download className="h-4 w-4 mr-2" />Export CSV</a></Button>} />
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setStatus(f)} data-testid={`lead-filter-${f}`}
            className={`h-9 px-4 rounded-full text-sm capitalize border transition-colors ${status === f ? "bg-foreground text-background" : "bg-card hover:bg-secondary"}`}>{f}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {STAGES.map(([v, label]) => (
          <button key={v || "all"} onClick={() => setStage(v)} data-testid={`lead-stage-filter-${v || "all"}`}
            className={`h-9 px-4 rounded-full text-xs border transition-colors ${stage === v ? "bg-primary text-primary-foreground" : "bg-card hover:bg-secondary"}`}>{label}</button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {SOURCES.map(([v, label]) => (
          <button key={v || "all"} onClick={() => setSource(v)} data-testid={`lead-source-filter-${v || "all"}`}
            className={`h-9 px-4 rounded-full text-xs border transition-colors ${source === v ? "bg-foreground text-background" : "bg-card hover:bg-secondary"}`}>{label}</button>
        ))}
      </div>
      <div className="bg-card border rounded-xl overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Received</TableHead><TableHead>Customer</TableHead><TableHead>Service</TableHead><TableHead className="min-w-[200px]">Address</TableHead>
            <TableHead>Measured</TableHead><TableHead>Estimate</TableHead><TableHead>Stage</TableHead><TableHead>Source</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((l) => (
              <TableRow key={l.id} className="cursor-pointer" onClick={() => nav(`/app/leads/${l.id}`)} data-testid={`lead-row-${l.id}`}>
                <TableCell className="whitespace-nowrap text-xs">{fmtDate(l.created_at)}</TableCell>
                <TableCell><Link to={`/app/leads/${l.id}`} className="font-medium hover:underline" onClick={(e) => e.stopPropagation()}>{l.name}</Link></TableCell>
                <TableCell className="text-sm">{l.service_label || "—"}</TableCell>
                <TableCell className="text-sm">{l.confirmed_service_address}</TableCell>
                <TableCell className="font-mono text-xs whitespace-nowrap">{qtyText(l)}</TableCell>
                <TableCell className="whitespace-nowrap">{price(l)}</TableCell>
                <TableCell><span className={`text-[11px] rounded-full px-2.5 py-1 whitespace-nowrap ${stageOf(l).cls}`} data-testid={`lead-stage-badge-${l.id}`}>{stageOf(l).label}</span></TableCell>
                <TableCell><span className={`text-[11px] rounded-full px-2.5 py-1 whitespace-nowrap ${l.lead_source === "field_quote" ? "bg-indigo-100 text-indigo-900" : "bg-secondary"}`} data-testid={`lead-source-badge-${l.id}`}>{sourceLabel(l)}</span></TableCell>
                <TableCell><span className={`text-xs rounded-full px-2.5 py-1 capitalize ${STATUS_STYLE[l.lead_status]}`} data-testid={`lead-status-badge-${l.id}`}>{l.lead_status}</span></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!loading && items.length === 0 && (status !== "all" || stage || source) && (
          <div className="p-12 text-center text-sm text-muted-foreground" data-testid="leads-filter-empty">No leads match these filters.</div>
        )}
        {!loading && items.length === 0 && status === "all" && !stage && !source && (
          <div className="p-12 text-center" data-testid="leads-empty-state">
            <p className="font-medium">No leads yet</p>
            <p className="text-sm text-muted-foreground mt-1">Install your widget or share your hosted quote link to start receiving requests.</p>
            <Button asChild variant="outline" className="mt-4"><Link to="/app/install">Go to Install</Link></Button>
          </div>
        )}
      </div>
      {cursor && <Button variant="outline" className="mt-4" onClick={() => load(cursor, true)} disabled={loading} data-testid="leads-load-more">Load more</Button>}
    </div>
  );
}
